import { getSupabase } from './supabase';
import { AppError, toAppError } from './errors';
import { isAppRole, type AppRole } from '../types/auth';
import type { Company } from '../types/company';

// Backend-derived application identity. Under the per-company membership model
// (ADMINISTRATION-DESIGN §3/§4), `auth_user_id` is no longer globally unique on `users` — a caller
// may hold one membership row per company. `loadIdentity` resolves the single membership scoped by
// the current request context (RLS/`x-company-id`, §4) and requires it to be `active`. Roles come
// from `user_roles` for that exact membership row.
export interface Identity {
  readonly actorId: string; // users.id — the p_actor passed to reporting RPCs
  readonly company: Company;
  readonly roles: readonly AppRole[];
  /** users.display_name; null when unset — callers fall back to the session email. */
  readonly displayName: string | null;
}

interface UserRow {
  id: string;
  company_id: string;
  membership_status: string | null;
  display_name: string | null;
}

interface CompanyRow {
  id: string;
  name: string;
}

interface UserRoleRow {
  roles: { code: string } | { code: string }[] | null;
}

function invalidContract(code: string): AppError {
  return new AppError('invalid_payload', code, code);
}

export async function loadIdentity(authUserId: string): Promise<Identity> {
  const supabase = getSupabase();

  const userRes = await supabase
    .from('users')
    .select('id, company_id, membership_status, display_name')
    .eq('auth_user_id', authUserId)
    .maybeSingle<UserRow>();
  if (userRes.error) {
    throw toAppError(userRes.error);
  }
  if (userRes.data === null) {
    throw toAppError({ message: 'IDENTITY_NOT_FOUND', code: 'IDENTITY_NOT_FOUND' });
  }
  // Fails closed for anything but 'active'. The 'invited' case gets its own code so the guard can
  // say "your account is not activated yet" instead of a generic failure — pre-0097 invitations
  // (D-156 made new ones land active) are the only way to reach it, and telling that person to
  // wait for an administrator is the whole difference between a dead end and an explanation.
  if (userRes.data.membership_status !== 'active') {
    const code =
      userRes.data.membership_status === 'invited'
        ? 'IDENTITY_MEMBERSHIP_INVITED'
        : 'IDENTITY_MEMBERSHIP_NOT_ACTIVE';
    throw new AppError('forbidden', code, code);
  }
  const { id: actorId, company_id: companyId } = userRes.data;
  // Blank/whitespace-only is treated as unset so the caller's email fallback engages.
  const displayName = (userRes.data.display_name ?? '').trim() || null;

  const [companyRes, rolesRes] = await Promise.all([
    supabase.from('companies').select('id, name').eq('id', companyId).maybeSingle<CompanyRow>(),
    supabase.from('user_roles').select('roles(code)').eq('user_id', actorId),
  ]);
  if (companyRes.error) {
    throw toAppError(companyRes.error);
  }
  if (rolesRes.error) {
    throw toAppError(rolesRes.error);
  }

  const company: Company = {
    id: companyId,
    name: companyRes.data?.name ?? companyId,
  };

  const roles: AppRole[] = [];
  for (const row of (rolesRes.data ?? []) as UserRoleRow[]) {
    const codes = Array.isArray(row.roles) ? row.roles : row.roles === null ? [] : [row.roles];
    for (const c of codes) {
      if (isAppRole(c.code) && !roles.includes(c.code)) {
        roles.push(c.code);
      }
    }
  }

  return { actorId, company, roles, displayName };
}

// ── Header-independent membership discovery (ADMINISTRATION-DESIGN §4 [C1]) ───────────────────────
// app.fn_list_my_memberships() takes no arguments and requires no active company/header — the
// only way a multi-membership caller can discover their own memberships before any company is
// selected. Never use ordinary RLS-scoped table reads for this.
export type MembershipStatus = 'invited' | 'active' | 'inactive';

export interface Membership {
  readonly companyId: string;
  readonly companyName: string;
  readonly membershipStatus: MembershipStatus;
  readonly isPreferred: boolean;
}

interface MembershipRow {
  company_id: unknown;
  company_name: unknown;
  membership_status: unknown;
  is_preferred: unknown;
}

const MEMBERSHIP_STATUSES: readonly MembershipStatus[] = ['invited', 'active', 'inactive'];

function isMembershipStatus(value: unknown): value is MembershipStatus {
  return typeof value === 'string' && (MEMBERSHIP_STATUSES as readonly string[]).includes(value);
}

export async function listMyMemberships(): Promise<readonly Membership[]> {
  const supabase = getSupabase();
  const res = await supabase.schema('app').rpc('fn_list_my_memberships');
  if (res.error) {
    throw toAppError(res.error);
  }

  if (!Array.isArray(res.data)) {
    throw invalidContract('IDENTITY_MALFORMED_MEMBERSHIP');
  }

  const seenCompanyIds = new Set<string>();
  const memberships: Membership[] = [];

  for (const entry of res.data) {
    if (typeof entry !== 'object' || entry === null || Array.isArray(entry)) {
      throw invalidContract('IDENTITY_MALFORMED_MEMBERSHIP');
    }
    const row = entry as MembershipRow;
    if (typeof row.company_id !== 'string' || row.company_id.trim().length === 0) {
      throw invalidContract('IDENTITY_MALFORMED_MEMBERSHIP');
    }
    if (typeof row.company_name !== 'string' || row.company_name.trim().length === 0) {
      throw invalidContract('IDENTITY_MALFORMED_MEMBERSHIP');
    }
    if (typeof row.is_preferred !== 'boolean') {
      throw invalidContract('IDENTITY_MALFORMED_MEMBERSHIP');
    }
    if (!isMembershipStatus(row.membership_status)) {
      throw invalidContract('IDENTITY_UNKNOWN_MEMBERSHIP_STATUS');
    }
    if (seenCompanyIds.has(row.company_id)) {
      throw invalidContract('IDENTITY_DUPLICATE_MEMBERSHIP');
    }
    if (row.is_preferred && row.membership_status !== 'active') {
      throw invalidContract('IDENTITY_INVALID_PREFERRED_MEMBERSHIP');
    }
    seenCompanyIds.add(row.company_id);

    memberships.push({
      companyId: row.company_id,
      companyName: row.company_name,
      membershipStatus: row.membership_status,
      isPreferred: row.is_preferred,
    });
  }

  return memberships;
}

// ── Active-company preference (ADMINISTRATION-DESIGN §4/§5) ────────────────────────────────────────
// app.fn_set_active_company persists the caller's client-read preference only — never
// authoritative, never consulted by current_company_id(). Does not touch the tab-local company
// context (companyContext.ts); the caller decides when/whether to apply the switch.
export async function setActiveCompanyPreference(companyId: string): Promise<void> {
  if (typeof companyId !== 'string' || companyId.trim().length === 0) {
    throw invalidContract('IDENTITY_INVALID_COMPANY_ID');
  }

  const supabase = getSupabase();
  const res = await supabase.schema('app').rpc('fn_set_active_company', { p_company_id: companyId });
  if (res.error) {
    throw toAppError(res.error);
  }

  const data = res.data as { ok?: unknown; company_id?: unknown } | null;
  if (data === null || data.ok !== true || data.company_id !== companyId) {
    throw invalidContract('IDENTITY_INVALID_PREFERENCE_RESPONSE');
  }
}
